import { useEffect, useMemo } from "react";
import BankManagement from "./BankManagement";
import ReligionManagement from "./ReligionManagement";
import JobTitleManagement from "./JobTitleManagement";
import JobGradeManagement from "./JobGradeManagement";
import PermissionContextFunctions from "../../../../assets/functions/PermissionContextFunctions";
import useUrlSync from "../../../../hooks/useUrlSync"; // Adjust path as needed
import "../../../../style/cssModules/company/Company.module.css";
import stylesMain from "../../../../style/cssModules/company/Company.module.css";

const TABS = {
  BANK: "bank",
  RELIGION: "religion",
  JOB_TITLE: "job-title",
  JOB_GRADE: "job-grade",
};

// Default filters for each tab - customize as needed
const defaultFilters = {
  status: {
    value: ["active", "inactive"],
    apiKey: "status",
    operator: "in",
  },
};

export default function CustomAddonsPage() {
  const { grantPermission } = PermissionContextFunctions();

  // Get permitted tabs based on permissions
  const permittedTabs = useMemo(() => {
    const tabs = [];
    if (grantPermission("Bank:read")) {
      tabs.push({ value: TABS.BANK, label: "Bank" });
    }
    if (grantPermission("Religion:read")) {
      tabs.push({ value: TABS.RELIGION, label: "Religion" });
    }
    if (grantPermission("JobTitle:read")) {
      tabs.push({ value: TABS.JOB_TITLE, label: "Job Title" });
    }
    if (grantPermission("JobGrade:read")) {
      tabs.push({ value: TABS.JOB_GRADE, label: "Job Grade" });
    }
    return tabs;
  }, [grantPermission]);

  // Use the URL sync hook with default tab as first permitted tab
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    addedFilters,
    setAddedFilters,
    activeTab,
    setActiveTab,
  } = useUrlSync({
    page: 1,
    limit: 10,
    filters: defaultFilters,
    tab: permittedTabs.length > 0 ? permittedTabs[0].value : TABS.BANK,
  });

  // Helper functions for filter management
  const addFilter = (
    filterKey,
    value,
    apiKey = filterKey,
    isQueryParam = false,
    operator = "eq"
  ) => {
    setCurrentPage(1); // Reset to first page when filtering
    setAddedFilters((prev) => ({
      ...prev,
      [filterKey]: { value, apiKey, isQueryParam, operator },
    }));
  };

  const removeFilter = (filterKey) => {
    setAddedFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  // Redirect to first permitted tab if current tab is not permitted
  useEffect(() => {
    if (
      permittedTabs.length > 0 &&
      !permittedTabs.some((tab) => tab.value === activeTab)
    ) {
      setActiveTab(permittedTabs[0].value);
    }
  }, [activeTab, permittedTabs, setActiveTab]);

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    // Reset pagination and filters when switching tabs
    setCurrentPage(1);
    setAddedFilters(defaultFilters);
  };

  if (permittedTabs.length === 0) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="content-container">
            <div className="text-center py-8">
              <p>You don't have permission to access any of these modules.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Tab content area */}
      {activeTab === TABS.BANK && grantPermission("Bank:read") && (
        <BankManagement
          tabsOptions={permittedTabs}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          // Pass URL sync props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          addedFilters={addedFilters}
          setAddedFilters={setAddedFilters}
          addFilter={addFilter}
          removeFilter={removeFilter}
        />
      )}
      {activeTab === TABS.RELIGION && grantPermission("Religion:read") && (
        <ReligionManagement
          tabsOptions={permittedTabs}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          // Pass URL sync props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          addedFilters={addedFilters}
          setAddedFilters={setAddedFilters}
          addFilter={addFilter}
          removeFilter={removeFilter}
        />
      )}
      {activeTab === TABS.JOB_TITLE && grantPermission("JobTitle:read") && (
        <JobTitleManagement
          tabsOptions={permittedTabs}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          // Pass URL sync props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          addedFilters={addedFilters}
          setAddedFilters={setAddedFilters}
          addFilter={addFilter}
          removeFilter={removeFilter}
        />
      )}
      {activeTab === TABS.JOB_GRADE && grantPermission("JobGrade:read") && (
        <JobGradeManagement
          tabsOptions={permittedTabs}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          // Pass URL sync props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          addedFilters={addedFilters}
          setAddedFilters={setAddedFilters}
          addFilter={addFilter}
          removeFilter={removeFilter}
        />
      )}
    </>
  );
}
